/**
 * Wire shapes for the calibration optimizer. Mirrors the types declared in
 * `server/labs/calibration-optimizer/proposal.ts` — kept in this client-side
 * file because the server types live behind a `@shared` boundary that the
 * client can't import directly (drizzle deps trip the bundler).
 */

export type DimSlug =
  | "academic"
  | "trajectory"
  | "tenure"
  | "founder"
  | "pm"
  | "sm"
  | "ba"
  | "hf"
  | "ps"
  | "role-level";

export type ProposalStatus = "pending" | "fitting" | "ready" | "accepted" | "rejected" | "error";

export interface FeaturePredicate {
  feature: string;
  op: ">=" | "<=" | ">" | "<" | "==" | "!=";
  value: number | boolean | string;
}

export interface Rung {
  grade: number;
  when: FeaturePredicate[];
  description: string;
}

export interface LadderMetrics {
  goldHitRate: number;
  looHitRate: number;
  looCi: { lo: number; hi: number };
  qwk: number | null;
  silverViolations: number;
  rungCount: number;
}

export interface LadderSnapshot {
  source: "db" | "disk" | "synth";
  yamlBody: string;
  version: number;
  rungs: Rung[];
  metrics: LadderMetrics;
}

export interface ChangedRung {
  grade: number;
  before: { when: FeaturePredicate[]; description: string };
  after: { when: FeaturePredicate[]; description: string };
}

export interface LadderDiff {
  addedRungs: Rung[];
  removedRungs: Rung[];
  changedRungs: ChangedRung[];
}

export interface FixtureAffected {
  candidateId: string;
  currentGrade: number | null;
  proposedGrade: number | null;
  targetGrade: number | null;
  withinHitRange: boolean | null;
}

export interface CorrectionCovered {
  correctionId: string;
  candidateName: string | null;
  evidenceId: string;
  targetGrade: number;
  proposedGrade: number | null;
  withinTolerance: boolean | null;
}

export interface OptimizationProposal {
  dim: DimSlug;
  status: ProposalStatus;
  current: LadderSnapshot;
  proposed: LadderSnapshot | null;
  diff: LadderDiff | null;
  fixturesAffected: FixtureAffected[];
  correctionsCovered: CorrectionCovered[];
  errorMessage?: string;
}

export interface OptimizerJobState {
  running: boolean;
  jobId?: string;
  startedAt?: string;
  finishedAt?: string | null;
  correctionsSnapshot: string[];
  proposals: OptimizationProposal[];
  fatalError?: string;
}

export interface RefitHints {
  blacklistedFeatures?: string[];
  lockedRungs?: { grade: number; when: FeaturePredicate[]; description: string }[];
  parsimony?: number;
}

export const DIM_LABEL: Record<DimSlug, string> = {
  academic: "Acadêmico",
  trajectory: "Trajetória",
  tenure: "Estabilidade",
  founder: "Fundador / construtor",
  pm: "Project management",
  sm: "Agency to influence stakeholders",
  ba: "Business acumen",
  hf: "HR fluency",
  ps: "Product sense",
  "role-level": "Nível agregado",
};
